import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import type { CartItemPrice } from '../contexts/TurnAuthoritySnapshotContext'
import { getCartItemDisplayPrice } from './cartItemPriceSelection'
import {
  countsTowardCartTotal,
  describeCartCheckoutBlocker,
  isCartPanelItemCheckoutReady,
  shouldIncludeCartPanelItemPriceEstimate,
  type CartPanelCheckoutReadinessItem,
  type CartPanelHotelCheckoutPrompt,
} from './cartPanelUtils'

export type CartItemAssistantPriceInput = CartPanelCheckoutReadinessItem & {
  tripItemId: number
  pricing: CartPanelCheckoutReadinessItem['pricing'] & {
    price: number | undefined
    memberPrice: number | undefined
  }
  includeInTotal: boolean
  productType: 'accommodation' | 'experience' | 'flight'
  checkoutPrompt?: CartPanelHotelCheckoutPrompt
}

export interface BuildCartItemAssistantPricesOptions {
  // Real LuxPlus entitlement, matching the cart panel and navbar cart total.
  hasLuxPlusBenefits: boolean
  currentCurrency: string
  currentRegionCode: string
}

/**
 * The charge this customer will actually face, not what any particular view is previewing.
 * The List view's toggle-driven preview price is a separate, visibility-scoped contribution
 * (CartDisplayedPrice) so an invisible List cannot report a price the customer is not looking at.
 */
export function buildCartItemAssistantPrices(
  items: Array<CartItemAssistantPriceInput>,
  options: BuildCartItemAssistantPricesOptions,
): Array<CartItemPrice> {
  const { hasLuxPlusBenefits, currentCurrency, currentRegionCode } = options

  return items.map((item) => {
    const checkoutPrice = getCartItemDisplayPrice(item.pricing, hasLuxPlusBenefits)

    const countsTowardTotal = countsTowardCartTotal({
      cartExcluded: item.cartExcluded,
      includeInTotal: shouldIncludeCartPanelItemPriceEstimate(item),
    })
    // The hotel prompt already resolves what an unrecognised status means for the customer.
    const blockerStatus = item.checkoutPrompt === 'add_dates' ? 'needs_dates' : item.cartStatus
    const checkoutBlocker = describeCartCheckoutBlocker(blockerStatus) ??
      (item.pricing.isAvailable === false ? 'Unavailable' : undefined)

    return {
      itemId: String(item.tripItemId),
      checkoutPrice: checkoutPrice === undefined ? undefined : formatCurrency(checkoutPrice, currentCurrency, currentRegionCode),
      countsTowardTotal,
      isBookingReady: isCartPanelItemCheckoutReady(item),
      ...(checkoutBlocker !== undefined ? { checkoutBlocker } : {}),
    }
  })
}
